import React, {useRef} from 'react';
import {observer} from 'mobx-react-lite';
import {TRACK_CLASSNAME} from "../../config/constants";
import {ApplicationStore} from "../../../../store/ApplicationStore";
import {convertUnitsToTime} from "../../../../utils/dom/convertUnitsToTime";
import {convertAbsTimeToUnits, convertTimeToUnits} from "../../../../utils/dom/convertTimeToUnits";
import './Track.styles.scss';

// Import your clip components here
// import Transition from "../Transition/Transition";
import {CompositionStateStore, UserClip} from "../../../../store/CompositionStateStore";
import VideoClip from "../VideoClip/VideoClip";
import AudioClip from "../AudioClip/AudioClip";
import ImageClip from "../ImageClip/ImageClip";
import TextClip from "../TextClip/TextClip";
import { PlaceholderTimelineClip } from "../PlaceholderClip/PlaceholderClip";
import { CompositionService } from "../../../../services/CompositionService";
import ScriptClip from "../../../../../pages/V2Editor/ScriptLayer/ScriptClip";
import CaptionsTimelineClip from "../../../../../pages/V2Editor/ScriptLayer/CaptionsTimelineClip";
import {useCreateDropClipPlaceholder} from "../../hooks/tracks/useCreateDropClipPlaceholder";
import { beginScriptAddMediaFlow } from "../../../../utils/scriptAddMediaFlow";

interface TrackProps {
    layerId: string;
    setContextMenuVisible: (visible: boolean) => void;
    setContextMenuPosition: (position: { x: number, y: number }) => void;
    timelineTracksRef: React.RefObject<HTMLDivElement>;
}

export const Track: React.FC<TrackProps> = observer(({ layerId }) => {
    const placeholderRef = useRef<HTMLDivElement>(null);
    const createDropClipPlaceholder = useCreateDropClipPlaceholder(() => placeholderRef.current);

    const layer = CompositionStateStore.layers[layerId];
    // const temporaryTransitions = layer ? TimelineService.getTemporaryTransitions(layerId) : [];
    const clipsIds = layer ? layer.clipIds : [];
    const transitionIds = [];
    const clips = clipsIds.reduce<Record<string, UserClip>>((acc, cv) => {
        acc[cv] = CompositionStateStore.clips[cv];
        return acc;
    }, {});

    const scriptLayerId = (CompositionService.isInitialized() && (CompositionService.composition as any).scriptLayer)
        ? (CompositionService.composition as any).scriptLayer.layerId
        : null;
    const isScriptLayer = scriptLayerId != null && scriptLayerId === layerId;
    
    // Check if this layer only contains overlay clips (captions/text) for thinner height.
    const isOverlayOnlyLayer = clipsIds.length > 0 && clipsIds.every(id => {
        const clip = CompositionStateStore.clips[id];
        const type = String(clip?.type ?? '').toLowerCase();
        return type === 'captions' || type === 'text' || type === 'placeholder';
    });
    
    // The script spine is intentionally roomier than ordinary clips without
    // reading as a separate editor panel.
    const trackHeight = isScriptLayer ? "112px" : isOverlayOnlyLayer ? "36px" : "56px";
    const scriptClipState = (() => {
        if (!isScriptLayer) return null;
        for (const id of clipsIds) {
            const clip = CompositionStateStore.clips[id];
            if (String(clip?.type ?? "").toLowerCase() === "script") return clip;
        }
        return null;
    })();
    const scriptClipEngine: any = scriptClipState ? CompositionService.getClipById(scriptClipState.id) : null;

    const scriptStartSecAbs = Number(scriptClipState?.startTime ?? 0);
    const scriptDurationSec = Number(scriptClipState?.trimmedDuration ?? 0);
    const scriptStartXPx = convertAbsTimeToUnits(scriptStartSecAbs);
    const scriptEndXPx = scriptStartXPx + convertTimeToUnits(Math.max(0, scriptDurationSec));
    const SCRIPT_ADD_BUTTON_SIZE_PX = 56;
    const SCRIPT_ADD_BUTTON_GAP_PX = 10;

    const showStartAddButton = Boolean(
        scriptClipState &&
            scriptDurationSec > 0,
    );
    const showEndAddButton = Boolean(
        scriptClipState &&
            scriptDurationSec > 0,
    );

    const handleBoundaryAddMediaClick = (side: "start" | "end", event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!scriptClipEngine) return;

        const clipInSec = Number(scriptClipEngine?.range?.[0]?.seconds ?? 0);
        const clipOutSec = Number(scriptClipEngine?.range?.[1]?.seconds ?? scriptClipEngine?.duration?.seconds ?? clipInSec);
        const atClipLocalSec = side === "start" ? clipInSec : clipOutSec;
        if (!Number.isFinite(atClipLocalSec) || atClipLocalSec < 0) return;

        beginScriptAddMediaFlow({
            atClipLocalSec,
            preferExistingEmptyScene: false,
            source: side === "start" ? "timeline-script-start-boundary" : "timeline-script-end-boundary",
        });
    };
    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // @ts-ignore
        createDropClipPlaceholder.onDragLeave(event);
        let librettoClipId = null;
        try {
            const draggingObject = ApplicationStore.draggingObject;
            let clipId: string | undefined;

            if (!draggingObject) {
                return null;
            }

            if (draggingObject.type === "media") {
                // const mediaId = draggingObject.payload.id;
                // const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
                // const clip = await RendleyService.addMediaToLayer(layerId, mediaId, startTime);
                // librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
                // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
                //     librettoClipId = GenerateLibrettoClipId();
                //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
                //     RendleyStore.addClipIdForLibrettoClip(librettoClipId, clip.id);
                //     RendleyStore.setLayerIdForLibrettoClip(librettoClipId, layer.id);
                //     RendleyStore.addLibrettoClipIdToLayer(librettoClipId, layer.id);
                // }
                // clipId = clip?.id;
            }

            if (draggingObject.type === "text-preset") {
                // const title = draggingObject.payload;
                // const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
                // const clip = await RendleyService.createLottieClip({
                //     dataUrl: title.dataUrl,
                //     propertiesUrl: title.propertiesUrl,
                //     startTime,
                //     layerId: layerId,
                // });
                // librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
                // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
                //     librettoClipId = GenerateLibrettoClipId();
                //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
                //     RendleyStore.addClipIdForLibrettoClip(librettoClipId, clip.id);
                //     RendleyStore.setLayerIdForLibrettoClip(librettoClipId, layer.id);
                //     RendleyStore.addLibrettoClipIdToLayer(librettoClipId, layer.id);
                // }
                // clipId = clip?.id;
            }
        } catch {
            return null;
        }
    };

    const ReadableClipType = (clipType: string) => {
        return String(clipType).toLowerCase();
    }


    const renderClip = (clipId: string, index: number) => {
        const clip = clips[clipId];
        if (!clip) return null;

        const type = ReadableClipType(clip.type);
        if (isScriptLayer) {
            // All clips on script layer use ScriptClip - it handles all types with proper range/offset
            return <ScriptClip key={clipId} clipId={clip.id} layerId={layerId} />;
        } else {
            if (type === "captions") return <CaptionsTimelineClip key={clipId} clipId={clip.id} layerId={layerId} />;
            if (type === "audio") return <AudioClip key={clipId} clipId={clip.id} layerId={layerId} />;
            if (type === "video") return <VideoClip key={clipId} clipId={clip.id} layerId={layerId} />;
            if (type === "image") return <ImageClip key={clipId} clipId={clip.id} layerId={layerId} />;
            if (type === "text") return <TextClip key={clipId} clipId={clip.id} layerId={layerId} />;
            if (type === "placeholder") return <PlaceholderTimelineClip key={clipId} clipId={clip.id} layerId={layerId} />;
        }
        return null;
    };


    // const renderTransition = (transitionId: string) => {
    //     return <Transition key={transitionId} type="permanent" permanentTransition={{ transitionId }} />;
    // };
    //
    // const renderTransitionPlaceholder = ({ startTime, layerId, clipId }: any) => {
    //     return <Transition key={startTime} type="temporary" temporaryTransition={{ startTime, layerId, clipId }} />;
    // };

    const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        return createDropClipPlaceholder.onDragEnter(event);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        return createDropClipPlaceholder.onDragOver(event);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        // @ts-ignore
        return createDropClipPlaceholder.onDragLeave(event);
    };

    return (
        <div
            id={layerId}
            className={`${TRACK_CLASSNAME} ${TRACK_CLASSNAME}-${layerId}`}
            style={{ ['--clip-height' as any]: trackHeight }}
        >
            <div
                className="track__content"
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                style={{ height: trackHeight }}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
                onMouseDown={(e) => {
                    // Only handle clicks on empty track space (not on clips which stopPropagation)
                    if (e.currentTarget === e.target) {
                        // Move playhead to clicked position
                        const clickX = e.nativeEvent.offsetX;
                        const clickedTimeRaw = convertUnitsToTime(clickX);
                        const durationSec = Number(CompositionStateStore.duration ?? 0);
                        const clickedTime = Math.max(
                            0,
                            Number.isFinite(durationSec) && durationSec > 0
                                ? Math.min(durationSec, clickedTimeRaw)
                                : clickedTimeRaw,
                        );
                        CompositionService.seek(clickedTime);
                        // Deselect any selected clip
                        ApplicationStore.clearSelectedClipId();
                    }
                }}
            >
                <div ref={placeholderRef} className="track__drop-clip-placeholder"></div>
                {clipsIds.map(renderClip)}
                {isScriptLayer && showStartAddButton && (
                    <button
                        type="button"
                        className="track__script-add-button track__script-add-button--start"
                        style={{ left: `${scriptStartXPx - SCRIPT_ADD_BUTTON_SIZE_PX - SCRIPT_ADD_BUTTON_GAP_PX}px` }}
                        title="Add media at start"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => handleBoundaryAddMediaClick("start", e)}
                    >
                        +
                    </button>
                )}
                {isScriptLayer && showEndAddButton && (
                    <button
                        type="button"
                        className="track__script-add-button track__script-add-button--end"
                        style={{ left: `${scriptEndXPx + SCRIPT_ADD_BUTTON_GAP_PX}px` }}
                        title="Add media at end"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                        onClick={(e) => handleBoundaryAddMediaClick("end", e)}
                    >
                        +
                    </button>
                )}
                {/*{transitionIds.map(renderTransition)}*/}
                {/*{temporaryTransitions.map(renderTransitionPlaceholder)}*/}
            </div>
        </div>
    );
});
